import { useState } from 'react'
import { supabase } from '../lib/supabaseClient'
import { validateCouponApplicability } from '../utils/couponCalculations'

export type CouponType = 'PARTNER' | 'USER'

export type Coupon = {
  id: string
  code: string
  type: CouponType
  partner_id: string | null
  valid_from: string
  valid_until: string
  partner_commission_pct: number
  member_discount_pct: number
  user_discount_pct: number | null
  max_usage_count: number | null
  current_usage_count: number
  is_active: boolean
  partner?: {
    id: string
    name: string
    address: string
    logo_url: string | null
    color_code: string
    status: string
  } | null
}

/**
 * Hook pour gérer les coupons dans le tunnel de réservation.
 *
 * En mode PUBLIC : saisie manuelle d'un code → valide-coupon type=USER
 * En mode PARTNER : chargement automatique du coupon PARTNER actif
 */
export const useCoupon = () => {
  const [coupon, setCoupon] = useState<Coupon | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  /**
   * Valider un code coupon (mode PUBLIC — USER coupons uniquement)
   * Retourne le coupon validé ou null si invalide.
   */
  const validateCoupon = async (code: string): Promise<Coupon | null> => {
    setLoading(true)
    setError(null)

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/validate-coupon`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ mode: 'PUBLIC', code: code.trim().toUpperCase() }),
        }
      )

      if (!response.ok) {
        const errorData = await response.json()
        setError(errorData.error || 'Coupon invalide')
        return null
      }

      const data = await response.json()
      const validatedCoupon = data.coupon

      if (!validatedCoupon) {
        setError('Coupon introuvable')
        return null
      }

      // Vérifier l'applicabilité
      const validation = validateCouponApplicability(validatedCoupon)
      if (!validation.valid) {
        setError(validation.error || 'Coupon invalide')
        return null
      }

      setCoupon(validatedCoupon)
      return validatedCoupon as Coupon
    } catch (err) {
      console.error('Error validating coupon:', err)
      setError('Erreur lors de la validation du coupon')
      return null
    } finally {
      setLoading(false)
    }
  }

  /**
   * Charger automatiquement le coupon PARTNER actif (mode PARTNER)
   */
  const loadPartnerCoupon = async (partnerId: string): Promise<Coupon | null> => {
    setLoading(true)
    setError(null)

    try {
      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/validate-coupon`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({ mode: 'PARTNER', partner_id: partnerId }),
        }
      )

      if (!response.ok) {
        console.error('Error loading partner coupon')
        return null
      }

      const data = await response.json()
      const partnerCoupon = data.coupon

      if (partnerCoupon) {
        setCoupon(partnerCoupon)
      }
      return partnerCoupon
    } catch (err) {
      console.error('Error loading partner coupon:', err)
      return null
    } finally {
      setLoading(false)
    }
  }

  /**
   * Supprimer le coupon actuel
   */
  const clearCoupon = () => {
    setCoupon(null)
    setError(null)
  }

  /**
   * Vérifier si un coupon est appliqué
   */
  const hasCoupon = coupon !== null

  return {
    coupon,
    loading,
    error,
    validateCoupon,
    loadPartnerCoupon,
    clearCoupon,
    hasCoupon,
  }
}
